import React, { useState, useEffect } from 'react';
import {
  Popover,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './TransactionFiltersDropdown.css';

const TransactionFiltersDropdown = ({
  anchorEl,
  onClose,
  onApply,
  filters,
  userRole,
  financialInstitutions,
  merchants,
  showCustomDatePicker,
}) => {
  const open = Boolean(anchorEl);
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    if (open) {
      setLocalFilters(filters);
    }
  }, [open, filters]);

  const handleChange = (field, value) => {
    setLocalFilters({ ...localFilters, [field]: value });
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const reset = {
      searchSessionId: '',
      searchTxHash: '',
      fiId: '',
      merchantId: '',
      status: '',
      dateFrom: null,
      dateTo: null,
    };
    setLocalFilters(reset);
    onApply(reset);
    onClose();
  };

  const isSuperAdmin = userRole === 'SuperAdmin';
  const isFIAdmin = userRole === 'FI Admin';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      className="transaction-filters-popover"
    >
      <div className="filters-popover-content">
        <Typography variant="subtitle2" className="filters-title">
          Filters
        </Typography>
        {showCustomDatePicker && (
          <div className="filter-section">
            <label className="section-label">Date Range</label>
            <div className="date-picker-row">
              <DatePicker
                selected={localFilters.dateFrom}
                onChange={(date) => handleChange('dateFrom', date)}
                selectsStart
                startDate={localFilters.dateFrom}
                endDate={localFilters.dateTo}
                maxDate={localFilters.dateTo || new Date()}
                dateFormat="MM/dd/yyyy"
                placeholderText="Start date"
                className="custom-date-picker"
              />
              <DatePicker
                selected={localFilters.dateTo}
                onChange={(date) => handleChange('dateTo', date)}
                selectsEnd
                startDate={localFilters.dateFrom}
                endDate={localFilters.dateTo}
                minDate={localFilters.dateFrom}
                maxDate={new Date()}
                dateFormat="MM/dd/yyyy"
                placeholderText="End date"
                className="custom-date-picker"
              />
            </div>
          </div>
        )}

        <div className="filter-section">
          <label className="section-label">Search</label>
          <TextField
            fullWidth
            label="Session ID"
            size="small"
            value={localFilters.searchSessionId}
            onChange={(e) => handleChange('searchSessionId', e.target.value)}
            className="filter-field"
          />
          <TextField
            fullWidth
            label="Transaction Hash"
            size="small"
            value={localFilters.searchTxHash}
            onChange={(e) => handleChange('searchTxHash', e.target.value)}
            className="filter-field"
          />
        </div>

        {isSuperAdmin && (
          <div className="filter-section">
            <label className="section-label">Financial Institution</label>
            <FormControl fullWidth size="small">
              <InputLabel>Select FI</InputLabel>
              <Select
                label="Select FI"
                value={localFilters.fiId}
                onChange={(e) => handleChange('fiId', e.target.value)}
              >
                <MenuItem value="">All Financial Institutions</MenuItem>
                {financialInstitutions.map((fi) => (
                  <MenuItem key={fi.id} value={fi.id}>
                    {fi.name || fi.businessName || `FI #${fi.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {(isSuperAdmin || isFIAdmin) && (
          <div className="filter-section">
            <label className="section-label">Merchant</label>
            <FormControl fullWidth size="small">
              <InputLabel>Select Merchant</InputLabel>
              <Select
                label="Select Merchant"
                value={localFilters.merchantId}
                onChange={(e) => handleChange('merchantId', e.target.value)}
              >
                <MenuItem value="">All Merchants</MenuItem>
                {merchants.map((merchant) => (
                  <MenuItem key={merchant.id || merchant.terra_account_id} value={merchant.id || merchant.terra_account_id}>
                    {merchant.business_name || merchant.account_name || merchant.name || `Account ${merchant.id || merchant.terra_account_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        <div className="filter-section">
          <label className="section-label">Status</label>
          <FormControl fullWidth size="small">
            <InputLabel>Select Status</InputLabel>
            <Select
              label="Select Status"
              value={localFilters.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </div>

        <Divider className="section-divider" />
        <div className="filters-actions">
          <Button size="small" onClick={handleReset} color="secondary">
            Reset
          </Button>
          <Button size="small" variant="contained" onClick={handleApply} className="apply-button">
            Apply
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default TransactionFiltersDropdown;
